import type { MusicVideoEffect } from '../musicVideoEffects';
import type { MusicVideoGrade, MusicVideoLyricPlace, MusicVideoLyricStyle } from '../musicVideoLook';
import type { ReleaseThemeId } from '../releaseThemes';
import {
  longVideoCoverGeometry,
  longVideoEndPose,
  longVideoEndProgress,
  type LongVideoBox,
  type LongVideoEndCardText,
} from './longVideoEndCard';
import { clamp01, outCubic } from './videoMotion';

/**
 * The music video (design `asset-redesign/z4 Music video - new`, owner answers 2026-10-08): a
 * long-format, whole-song video of bank footage cut to the song. Two modes (owner decision
 * 2026-10-08):
 *
 * - `clean` (the default, also for a request with no mode): pure footage cut to the song. No title
 *   card, no bug, no end card, no CTA and no SmartLink. On the Free plan a small corner watermark
 *   shows on every frame.
 * - `songInfo`: design z4. A title card (the framed cover, the title as the hero, the artist and the
 *   bars, bottom left on a scrim) to the downbeat after 4 s; then the bug (cover 84 px, 16 bars, the
 *   id line) at the bottom left on every frame; the shared end card of the long videos for the last
 *   20 s from a downbeat (`longVideoEndCard.ts`) over the blurred footage.
 *
 * In both modes the cuts follow the beat grid of the theme and are twice as long in a calm section;
 * a section start is always a cut; only a section change has the transition of the theme. Every size
 * is in px of the 1920 x 1080 frame. Pure.
 */

export const MUSIC_VIDEO_MODES = ['clean', 'songInfo'] as const;
export type MusicVideoMode = (typeof MUSIC_VIDEO_MODES)[number];
/** A request with no mode (an old client) makes the clean music video. */
export const DEFAULT_MUSIC_VIDEO_MODE: MusicVideoMode = 'clean';

export function isMusicVideoMode(value: unknown): value is MusicVideoMode {
  return typeof value === 'string' && (MUSIC_VIDEO_MODES as readonly string[]).includes(value);
}

/** The mode of a stored or requested value: anything that is not `songInfo` is clean. */
export function musicVideoModeOf(value: unknown): MusicVideoMode {
  return isMusicVideoMode(value) ? value : DEFAULT_MUSIC_VIDEO_MODE;
}

/**
 * The cut grid of each theme in beats (page z4 `paceBeats`): Clean, Dreamy, Warm and Handmade every
 * 2 bars, Noir and Retro every bar, Neon every 2 beats, Hype every beat. A calm section (energy 1 or 2)
 * doubles it.
 */
export const MUSIC_VIDEO_PACE_BEATS: Readonly<Record<ReleaseThemeId, number>> = {
  clean: 8,
  hype: 1,
  dreamy: 8,
  dark: 4,
  warm: 8,
  retro: 4,
  neon: 2,
  handmade: 8,
};

/** The beats of one shot of the theme in a section of `energy` (1..5). Pure. */
export function musicVideoShotBeats(themeId: string, energy: number): number {
  const beats = (MUSIC_VIDEO_PACE_BEATS as Record<string, number | undefined>)[themeId] ?? 8;
  return energy <= 2 ? beats * 2 : beats;
}

/** The title card holds at least this long: it ends on the first downbeat at or after 4 s. */
export const MUSIC_VIDEO_TITLE_MIN_SEC = 4;

/** The texts and the timing of the song info of a music video (`timeline.kitMusicVideo`). */
export interface KitMusicVideoInfo {
  kind: 'musicVideo';
  /** `clean`: footage only (and the Free corner watermark); `songInfo`: the title card, the bug and the end card. */
  mode: MusicVideoMode;
  /** The end card texts: the title, the artist, the pill by date and the SmartLink address. */
  text: LongVideoEndCardText;
  /** The title card holds to this downbeat; the bug comes in over one bar from here. */
  titleEndSec: number;
  /** The shared end card starts on this downbeat. Null: a video too short for an end card. */
  endCardSec: number | null;
  /** One bar in seconds: the moves of the title, the bug and the end card take one bar. */
  barSec: number;
  /**
   * A music video of the editor (spec 2026-10-09 music video editor §4.5): which parts of the song
   * info show. Absent (a one-shot music video): every part of the mode.
   */
  parts?: KitMusicVideoParts;
  /**
   * A music video of the editor: the name shows alone in this window after the title card
   * (`musicVideoTextTimes`); the title card then shows the title only. Absent: the title card shows
   * the title and the artist together.
   */
  nameWindow?: { startSec: number; endSec: number };
  /** A music video of the editor: its look (the grade, the grain, the light). Absent: no look. */
  look?: KitMusicVideoLook;
  /** A music video of the editor: the texts that the user added, in video seconds. Absent: none. */
  customTexts?: KitMusicVideoCustomText[];
  /** A music video of the editor: how the lyric captions look. Absent: the caption style of the theme. */
  lyricStyle?: KitMusicVideoLyricStyle;
  /**
   * A music video of the editor: the effects of the doc (`musicVideoEffects.ts`) in video seconds,
   * cut to the video. A card with an empty text has the song value here. Absent (a one-shot music
   * video, a plan from before the effects): no effect.
   */
  effects?: MusicVideoEffect[];
  /** The beats of the song in video seconds: the effects hit on them. Absent: no beat pulse. */
  beats?: number[];
  /** The lyric lines that the lyric effects show, in video seconds. Absent: no line. */
  effectLines?: KitMusicVideoEffectLine[];
}

/**
 * One lyric line for the lyric effects, in video seconds, with the display lead of the captions
 * (`buildLyricLayers`): the line shows from `startSec` to `endSec`, each word from its `startSec`.
 */
export interface KitMusicVideoEffectLine {
  /** The `lyricTiming` line index. */
  lineIndex: number;
  text: string;
  startSec: number;
  endSec: number;
  words: KitMusicVideoEffectWord[];
}

export interface KitMusicVideoEffectWord {
  text: string;
  startSec: number;
  endSec: number;
}

/** The parts of the song info of an editor music video (`MusicVideoDoc.texts`). */
export interface KitMusicVideoParts {
  /** The title card at the start. */
  title: boolean;
  /** The small "Artist · Title" bug after the title card (`songInfoCorner`). */
  bug: boolean;
  /** The shared end card (`endCard.on`). */
  endCard: boolean;
}

/**
 * The look of an editor music video (`MusicVideoDoc.look` and the fades of `settings`), in video
 * seconds. The renderer puts the grade filter of `musicVideoLook.ts` on the footage (the same table
 * as the web preview).
 */
export interface KitMusicVideoLook {
  grade: MusicVideoGrade;
  grain: boolean;
  vignette: boolean;
  /** A white flash on the first beat of each chorus (`flashOnDrop`). Empty: no flash. */
  flashSec: number[];
  /** A light leak on each section change (`lightLeaks`). Empty: no light leak. */
  leakSec: number[];
  /** The picture fades in from black at the start and out to black at the end. */
  fadeIn: boolean;
  fadeOut: boolean;
}

/** One text that the user added to an editor music video. */
export interface KitMusicVideoCustomText {
  text: string;
  startSec: number;
  endSec: number;
}

/** The lyric caption style of an editor music video (`MusicVideoDoc.lyrics`). */
export interface KitMusicVideoLyricStyle {
  style: MusicVideoLyricStyle;
  where: MusicVideoLyricPlace;
}

/** The title card ends on the first downbeat at or after 4 s (video time); else at 4 s. Pure. */
export function musicVideoTitleEndSec(downbeats: readonly number[]): number {
  const downbeat = downbeats.find((time) => time >= MUSIC_VIDEO_TITLE_MIN_SEC - 0.01);
  return Math.round((downbeat ?? MUSIC_VIDEO_TITLE_MIN_SEC) * 1000) / 1000;
}

/** The places of the song info parts in one pose (WideFrame `mvTitlePose`, `mvBugPose`, `endPose`). */
export interface MusicVideoPoseBoxes {
  cover: LongVideoBox;
  bars: LongVideoBox;
  /** The title card: the title (132 px times the display factor) over the artist (48 px). */
  title: LongVideoBox;
  /** The id line: left-aligned in the bug, centred in the end card. */
  id: LongVideoBox;
  cta: LongVideoBox;
}

function titlePose(themeId: string): MusicVideoPoseBoxes {
  const geo = longVideoCoverGeometry(themeId);
  const cw = 250 * geo.coverFactor;
  const ext = geo.extra * 0.55;
  // The text starts 50 px right of the cover (and of the Retro record).
  const tx = 160 + cw * (geo.recordOffset < 0 ? 1.36 : 1) + 50;
  return {
    cover: { x: 160, y: 880 - cw - ext, w: cw, h: cw },
    bars: { x: tx, y: 820, w: 300, h: 60 },
    title: { x: tx, y: 520, w: 1760 - tx, h: 280 },
    id: { x: 160, y: 830, w: 700, h: 52 },
    cta: { x: 120, y: 760, w: 660, h: 130 },
  };
}

function bugPose(themeId: string): MusicVideoPoseBoxes {
  const geo = longVideoCoverGeometry(themeId);
  return {
    cover: { x: 150, y: geo.extra > 60 ? 790 : 802, w: 84, h: 84 },
    bars: { x: 260, y: 812, w: 150, h: 64 },
    title: { x: 460, y: 520, w: 1200, h: 280 },
    id: { x: 432, y: 818, w: 900, h: 52 },
    cta: { x: 120, y: 760, w: 660, h: 130 },
  };
}

function endPose(themeId: string, watermark: boolean): MusicVideoPoseBoxes {
  const end = longVideoEndPose(themeId, watermark);
  return { ...end, title: { x: 820, y: 180, w: 980, h: 400 } };
}

const lerp = (a: number, b: number, x: number): number => a + (b - a) * x;
const inOut = (x: number): number => (x < 0.5 ? 4 * x * x * x : 1 - (-2 * x + 2) ** 3 / 2);

function mixBox(a: LongVideoBox, b: LongVideoBox, x: number): LongVideoBox {
  return { x: lerp(a.x, b.x, x), y: lerp(a.y, b.y, x), w: lerp(a.w, b.w, x), h: lerp(a.h, b.h, x) };
}

function mixPose(a: MusicVideoPoseBoxes, b: MusicVideoPoseBoxes, x: number): MusicVideoPoseBoxes {
  return {
    cover: mixBox(a.cover, b.cover, x),
    bars: mixBox(a.bars, b.bars, x),
    title: mixBox(a.title, b.title, x),
    id: mixBox(a.id, b.id, x),
    cta: mixBox(a.cta, b.cta, x),
  };
}

/** What the song info layer draws at one time. */
export interface MusicVideoPose {
  boxes: MusicVideoPoseBoxes;
  /** The phase for the small frame of the bug (no record, no tape, a thin mat). */
  phase: 'title' | 'bug' | 'end';
  /** The opacity of each part. */
  cover: number;
  bars: number;
  title: number;
  id: number;
  cta: number;
  /** The title rises 30 px as it comes in. */
  titleRisePx: number;
  /** True: the id line sits at the left (the bug); false: centred (the end card). */
  idLeft: boolean;
  /** The scrim over the footage: the left gradient of the title and the end card, else the foot of the bug. */
  scrim: { kind: 'left' | 'foot'; opacity: number };
  /** The footage filter of the end card. */
  footage: { blurPx: number; brightness: number };
}

/**
 * The pose of the song info at the video time `t` (WideFrame, kind `mv`): the title card, the move
 * into the bug over one bar from `titleEndSec`, the bug, and the move into the shared end card over
 * one bar from `endCardSec`. The bars and the id line never travel across the cover: they fade out
 * in the first quarter of a move and in at the new place in the last quarter. Pure.
 */
export function musicVideoPose(
  t: number,
  info: Pick<KitMusicVideoInfo, 'titleEndSec' | 'endCardSec' | 'barSec'>,
  themeId: string,
  watermark: boolean
): MusicVideoPose {
  const bar = Math.max(0.1, info.barSec);
  const titleP = clamp01((t - info.titleEndSec) / bar);
  const end = longVideoEndProgress(t, info.endCardSec, bar);
  const endP = end.move;
  const title = titlePose(themeId);
  const bug = bugPose(themeId);
  const final = endPose(themeId, watermark);
  let boxes = mixPose(mixPose(title, bug, inOut(titleP)), final, inOut(endP));
  // The bars and the id line jump at the middle of a move (they are hidden then).
  const move = titleP > 0 && titleP < 1 ? titleP : endP > 0 && endP < 1 ? endP : -1;
  const hiddenMove = move < 0 ? 1 : move < 0.5 ? clamp01(1 - 4 * move) : clamp01(4 * move - 3);
  let barsBox = titleP < 0.5 ? title.bars : bug.bars;
  let idBox = boxes.id;
  if (endP > 0) {
    barsBox = endP < 0.5 ? bug.bars : final.bars;
    idBox = endP < 0.5 ? bug.id : final.id;
  }
  boxes = { ...boxes, bars: barsBox, id: idBox };
  const titleIn = outCubic(clamp01(t / 0.5));
  const titleOut = 1 - outCubic(clamp01(titleP * 2));
  const idTarget = lerp(0, 1, inOut(titleP));
  const idOpacity = clamp01((idTarget - 0.5) * 2) * (endP > 0 && endP < 1 ? hiddenMove : 1);
  const phase: MusicVideoPose['phase'] = endP >= 0.5 ? 'end' : titleP >= 0.5 ? 'bug' : 'title';
  const bugSteady = titleP >= 1 && endP <= 0;
  return {
    boxes,
    phase,
    cover: titleIn,
    bars: titleIn * hiddenMove,
    title: titleOut * titleIn,
    id: idOpacity,
    cta: end.cta,
    titleRisePx: Math.round(30 * (1 - titleIn) * 100) / 100,
    idLeft: endP < 0.5,
    scrim: bugSteady
      ? { kind: 'foot', opacity: 1 }
      : { kind: 'left', opacity: Math.round(Math.max(titleIn * (1 - titleP) * 0.9, endP * 0.95) * 1000) / 1000 },
    footage: { blurPx: end.blurPx, brightness: end.brightness },
  };
}
